// src/app/RootAppShell.tsx

"use client";

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter, usePathname, useSearchParams } from "next/navigation";

import { useAppSelector } from "../redux/hooks";
import store from "../redux/store";
import { AppDispatch } from "../redux/storeTypes";

import {
  NO_REDIRECT_PREFIXES,
  NO_REDIRECT_EXACT_ROUTES,
} from "../lib/routing/noRedirectRoutes";
import websocketManager, { WsModule } from "../lib/websocketManager";

import { getTermsAndPolicies } from "../redux/main/mainSlice";
import {
  setLoggedIn,
  setHasCompletedSignup,
  setUser,
  setLogout,
  fetchAuthMe,
} from "../redux/auth/authSlice";
import {
  getMemberProfile,
  getGuestProfile,
} from "../redux/profiles/profileSlice";
import { clearPresence } from "../redux/presence/presenceSlice";
import { clearAllStatuses } from "../redux/conversation/conversationSlice";
import { initializeConversationWsEvents } from "../realtime/conversationSocket";
import { initializePresenceEvents } from "../realtime/presenceSocket";
import { initNotificationsRealtime } from "../redux/notifications/notificationsSlice";
import { isOwnerProfileTemporarilyUnavailable } from "../utils/profiles/profileGate";
import { setPrivateIntegrationGateState } from "../utils/security/privateIntegrationGate";

import {
  selectIsLoggedIn,
  selectAuthUser,
} from "../redux/selectors/profile/userSelectors";

import useWebPush from "../hooks/web-push/useWebPush";
import { initFirebase } from "../lib/firebase/firebaseClient";

import DeviceKeyFlowManager from "../components/security/DeviceKeyFlowManager";
import IconBar from "../components/profiles/shared/navigation/IconBar";

import PublicLayout from "../components/layouts/PublicLayout";
import PrivateLayout from "../components/layouts/PrivateLayout";
import { getProfileType } from "@/redux/profiles/profileBootstrapSlice";

function toArray<T>(value: T | T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : value == null ? [] : [value];
}

function isLanguageOnboardingFinished(user: any | null): boolean {
  if (!user) return false;

  /*
   * Only an explicit false blocks access.
   * Missing values belong to legacy users and are treated as completed.
   */
  return user.language_onboarding_completed !== false;
}

export default function RootAppShell({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch: AppDispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const postLoginProfileBootstrapRef = useRef<string | null>(null);

  const isLoggedIn = useSelector(selectIsLoggedIn);
  const user = useAppSelector(selectAuthUser);

  const rawProfile = useAppSelector((state) => state.profile.profile) as any;

  const [initialLoading, setInitialLoading] = useState(true);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(true);
  const [webSocketError, setWebSocketError] = useState<string | null>(null);

  const kids = toArray(children) as React.ReactNode[];
  const cleanPathname = pathname.split("?")[0];

  const isNoRedirectPath =
    NO_REDIRECT_EXACT_ROUTES.includes(cleanPathname) ||
    NO_REDIRECT_PREFIXES.some((prefix) => cleanPathname.startsWith(prefix));

  const isMemberUser = Boolean(user?.is_member);

  const languageOnboardingCompleted = isLanguageOnboardingFinished(user);

  /*
   * The user is authenticated after choose-path, but is not allowed into the
   * private application until language onboarding is completed.
   */
  const requiresLanguageOnboarding = Boolean(
    isLoggedIn &&
    user &&
    !user.is_deleted &&
    !user.is_suspended &&
    !languageOnboardingCompleted,
  );

  const isLanguageOnboardingPath = cleanPathname === "/choose-language";

  const shouldBlockForLanguageRedirect =
    requiresLanguageOnboarding && !isLanguageOnboardingPath;

  const isRestrictionResolutionPending = Boolean(
    isLoggedIn &&
    user &&
    languageOnboardingCompleted &&
    isMemberUser &&
    !user.is_deleted &&
    !user.is_suspended &&
    !rawProfile,
  );

  const isRestrictedOwnerProfile = Boolean(
    isLoggedIn &&
    user &&
    languageOnboardingCompleted &&
    isMemberUser &&
    rawProfile &&
    isOwnerProfileTemporarilyUnavailable(rawProfile),
  );

  const isRestrictedAllowedPath = (path: string) => {
    if (path === "/") return true;
    if (path === "/reactivate-account") return true;

    return false;
  };

  const shouldBlockRestrictedPath =
    isRestrictedOwnerProfile && !isRestrictedAllowedPath(cleanPathname);

  /*
   * App chrome includes PrivateLayout behavior and IconBar.
   * Authentication alone is intentionally not enough.
   */
  const canUseAppChrome = Boolean(
    isLoggedIn &&
    user &&
    languageOnboardingCompleted &&
    !user.is_deleted &&
    !user.is_suspended,
  );

  const canRunPrivateIntegrations = Boolean(
    canUseAppChrome &&
    !isRestrictionResolutionPending &&
    !isRestrictedOwnerProfile,
  );

  const shouldBootRealtime = canRunPrivateIntegrations;

  useWebPush(canRunPrivateIntegrations ? user?.id : undefined);

  useEffect(() => {
    if (!isLoggedIn || !user) {
      setPrivateIntegrationGateState("logged_out", null);
      return;
    }

    if (user.is_deleted || user.is_suspended) {
      setPrivateIntegrationGateState("logged_out", user.id);
      return;
    }

    /*
     * Language onboarding is still part of the public signup boundary.
     * Do not allow private integrations before it is completed.
     */
    if (requiresLanguageOnboarding) {
      setPrivateIntegrationGateState("logged_out", user.id);
      return;
    }

    if (isRestrictionResolutionPending) {
      setPrivateIntegrationGateState("pending", user.id);
      return;
    }

    if (isRestrictedOwnerProfile) {
      setPrivateIntegrationGateState("restricted", user.id);
      return;
    }

    setPrivateIntegrationGateState("allowed", user.id);
  }, [
    isLoggedIn,
    user?.id,
    user?.is_deleted,
    user?.is_suspended,
    requiresLanguageOnboarding,
    isRestrictionResolutionPending,
    isRestrictedOwnerProfile,
  ]);

  useEffect(() => {
    initFirebase();
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    navigator.serviceWorker
      .register("/sw.js?v=2026-08-brand-assets-refresh")
      .then((registration) => {
        registration.update();
      })
      .catch(() => {
        // Ignore SW registration failures.
      });
  }, []);

  useEffect(() => {
    const writeCookie = () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      document.cookie = `access_token=${token}; Path=/; Max-Age=300; Secure; SameSite=Lax`;
    };

    writeCookie();

    const intervalID = setInterval(writeCookie, 60_000);

    return () => clearInterval(intervalID);
  }, []);

  useEffect(() => {
    const updateWebSocketStatus = () => {
      const isConnected = websocketManager.isConnected();

      setIsWebSocketConnected(isConnected);

      if (!isConnected) {
        setWebSocketError("⚠️ Connection lost. Trying to reconnect...");
      } else {
        setWebSocketError(null);
      }
    };

    updateWebSocketStatus();

    const intervalID = setInterval(updateWebSocketStatus, 3000);

    return () => clearInterval(intervalID);
  }, []);

  useEffect(() => {
    if (user?.is_deleted && pathname !== "/reactivate-account") {
      router.replace("/reactivate-account");
    }
  }, [user?.is_deleted, pathname, router]);

  /*
   * Mandatory language-onboarding boundary.
   *
   * Even when the user manually enters /settings, /conversation, /moments,
   * /square, /streams, /, or a profile URL, they are returned here.
   */
  useEffect(() => {
    if (initialLoading) return;
    if (!requiresLanguageOnboarding) return;
    if (isLanguageOnboardingPath) return;

    router.replace("/choose-language");
  }, [
    initialLoading,
    requiresLanguageOnboarding,
    isLanguageOnboardingPath,
    router,
  ]);

  /*
   * A completed user should not be able to reopen onboarding manually.
   */
  useEffect(() => {
    if (initialLoading) return;
    if (!isLoggedIn || !user) return;
    if (user.is_deleted || user.is_suspended) return;
    if (!languageOnboardingCompleted) return;
    if (!isLanguageOnboardingPath) return;

    router.replace("/");
  }, [
    initialLoading,
    isLoggedIn,
    user,
    languageOnboardingCompleted,
    isLanguageOnboardingPath,
    router,
  ]);

  useEffect(() => {
    if (!isLoggedIn || !user) return;
    if (requiresLanguageOnboarding) return;
    if (!isRestrictedOwnerProfile) return;

    if (shouldBlockRestrictedPath) {
      router.replace("/");
    }
  }, [
    isLoggedIn,
    user?.id,
    requiresLanguageOnboarding,
    isRestrictedOwnerProfile,
    shouldBlockRestrictedPath,
    router,
  ]);

  /*
   * Initial application bootstrap.
   * This effect intentionally does not depend on pathname.
   */
  useEffect(() => {
    let cancelled = false;

    const initializeAppState = async () => {
      setInitialLoading(true);

      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");
      const savedUser = localStorage.getItem("user");

      if (!accessToken || !refreshToken || !savedUser) {
        setPrivateIntegrationGateState("logged_out", null);
        dispatch(setLogout());

        await dispatch(getTermsAndPolicies());

        if (!cancelled) {
          setInitialLoading(false);
        }

        return;
      }

      try {
        const parsedUser = JSON.parse(savedUser);

        dispatch(setUser(parsedUser));
        dispatch(setLoggedIn(true));

        const parsedLanguageCompleted =
          parsedUser?.language_onboarding_completed !== false;

        if (parsedLanguageCompleted) {
          dispatch(setHasCompletedSignup());
          localStorage.removeItem("language_onboarding_pending");
        } else {
          localStorage.setItem("language_onboarding_pending", "true");
          setPrivateIntegrationGateState("logged_out", parsedUser?.id ?? null);
        }

        /*
         * fetchAuthMe is the authoritative source. Its reducer should update
         * user and hasCompletedSignup from the current backend response.
         */
        const freshUser = await dispatch(fetchAuthMe()).unwrap();

        if (cancelled) return;

        const freshLanguageCompleted =
          freshUser?.language_onboarding_completed !== false;

        if (freshLanguageCompleted) {
          dispatch(setHasCompletedSignup());
          localStorage.removeItem("language_onboarding_pending");
        } else {
          localStorage.setItem("language_onboarding_pending", "true");
          setPrivateIntegrationGateState(
            "logged_out",
            freshUser?.id ?? parsedUser?.id ?? null,
          );
        }

        await dispatch(getTermsAndPolicies());

        if (cancelled) return;

        /*
         * Never bootstrap the private profile before language onboarding.
         */
        if (
          freshLanguageCompleted &&
          !freshUser?.is_deleted &&
          !freshUser?.is_suspended
        ) {
          if (freshUser?.is_member) {
            setPrivateIntegrationGateState("pending", freshUser.id);
          } else {
            setPrivateIntegrationGateState("allowed", freshUser?.id ?? null);
          }

          const result = await dispatch(getProfileType()).unwrap();

          if (cancelled) return;

          if (result.profile_type === "member") {
            const member = await dispatch(getMemberProfile()).unwrap();

            if (cancelled) return;

            if (isOwnerProfileTemporarilyUnavailable(member)) {
              setPrivateIntegrationGateState("restricted", freshUser.id);
            } else {
              setPrivateIntegrationGateState("allowed", freshUser.id);
            }
          } else if (result.profile_type === "guest") {
            await dispatch(getGuestProfile()).unwrap();

            if (cancelled) return;

            setPrivateIntegrationGateState("allowed", freshUser.id);
          } else {
            setPrivateIntegrationGateState("pending", freshUser.id);
          }
        }
      } catch {
        setPrivateIntegrationGateState("logged_out", null);

        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        localStorage.removeItem("is_member");
        localStorage.removeItem("language_onboarding_pending");

        dispatch(setLogout());
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };

    initializeAppState();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  /*
   * Protect authenticated-only URLs for fully logged-out visitors.
   * Language-incomplete users are handled separately by the onboarding guard.
   */
  useEffect(() => {
    if (initialLoading) return;

    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");
    const savedUser = localStorage.getItem("user");

    const requiresAuthenticatedShell =
      pathname.startsWith("/conversation") ||
      pathname.startsWith("/settings") ||
      pathname.startsWith("/moments") ||
      pathname.startsWith("/square") ||
      pathname.startsWith("/streams");

    if (
      requiresAuthenticatedShell &&
      (!accessToken || !refreshToken || !savedUser)
    ) {
      router.replace("/");
    }
  }, [initialLoading, pathname, router]);

  /*
   * Bootstrap the profile after login or language completion without needing
   * a full page refresh.
   */
  useEffect(() => {
    if (initialLoading) return;
    if (!isLoggedIn) return;
    if (!user) return;
    if (requiresLanguageOnboarding) return;
    if (!languageOnboardingCompleted) return;
    if (user.is_deleted || user.is_suspended) return;
    if (rawProfile) return;

    const bootKey = `${user.id}:${user.is_member ? "member" : "guest"}`;

    if (postLoginProfileBootstrapRef.current === bootKey) {
      return;
    }

    postLoginProfileBootstrapRef.current = bootKey;

    let cancelled = false;

    const bootstrapProfileAfterLogin = async () => {
      try {
        if (user.is_member) {
          setPrivateIntegrationGateState("pending", user.id);
        } else {
          setPrivateIntegrationGateState("allowed", user.id);
        }

        const result = await dispatch(getProfileType()).unwrap();

        if (cancelled) return;

        if (result.profile_type === "member") {
          const member = await dispatch(getMemberProfile()).unwrap();

          if (cancelled) return;

          if (isOwnerProfileTemporarilyUnavailable(member)) {
            setPrivateIntegrationGateState("restricted", user.id);
          } else {
            setPrivateIntegrationGateState("allowed", user.id);
          }

          return;
        }

        if (result.profile_type === "guest") {
          await dispatch(getGuestProfile()).unwrap();

          if (cancelled) return;

          setPrivateIntegrationGateState("allowed", user.id);
          return;
        }

        setPrivateIntegrationGateState("pending", user.id);
      } catch {
        if (cancelled) return;

        if (user.is_member) {
          setPrivateIntegrationGateState("pending", user.id);
        } else {
          setPrivateIntegrationGateState("logged_out", user.id);
        }
      }
    };

    bootstrapProfileAfterLogin();

    return () => {
      cancelled = true;
    };
  }, [
    initialLoading,
    isLoggedIn,
    languageOnboardingCompleted,
    requiresLanguageOnboarding,
    user?.id,
    user?.is_member,
    user?.is_deleted,
    user?.is_suspended,
    rawProfile,
    dispatch,
  ]);

  useEffect(() => {
    if (initialLoading) return;
    if (requiresLanguageOnboarding) return;
    if (!languageOnboardingCompleted) return;
    if (isRestrictionResolutionPending) return;
    if (isRestrictedOwnerProfile) return;
    if (!isLoggedIn || !user || user.is_deleted || user.is_suspended) return;

    const nextParam = searchParams.get("next");
    if (!nextParam) return;

    let target = "/";

    try {
      target = decodeURIComponent(nextParam);
    } catch {
      target = nextParam;
    }

    if (!target || target === pathname) return;

    router.replace(target);
  }, [
    initialLoading,
    requiresLanguageOnboarding,
    languageOnboardingCompleted,
    isRestrictionResolutionPending,
    isRestrictedOwnerProfile,
    isLoggedIn,
    user,
    pathname,
    router,
    searchParams,
  ]);

  /*
   * Presence, conversation and notification WebSockets.
   */
  useEffect(() => {
    if (!shouldBootRealtime) {
      dispatch(clearPresence());
      dispatch(clearAllStatuses());
      websocketManager.disconnectAll();
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) return;

    const modules: WsModule[] = ["presence", "conversation", "notifications"];

    let isAlive = true;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 10;
    const reconnectTimeouts = new Set<ReturnType<typeof setTimeout>>();

    const connectWebSocket = () => {
      if (!isAlive) return;

      modules.forEach((module) => {
        if (!isAlive) return;

        if (!websocketManager.isConnected()) {
          websocketManager.connect(token, module);
        }

        if (!websocketManager.hasEventsRegistered(module)) {
          if (module === "presence") {
            initializePresenceEvents(dispatch);
          }

          if (module === "conversation") {
            initializeConversationWsEvents(dispatch, () => store.getState());
          }

          if (module === "notifications") {
            dispatch(initNotificationsRealtime(token));
          }

          websocketManager.markEventsAsRegistered(module);
        }
      });
    };

    const reconnectWebSocket = () => {
      if (!isAlive) return;
      if (reconnectAttempts >= maxReconnectAttempts) return;
      if (websocketManager.isConnected()) return;

      const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);
      const jitter = Math.random() * 5000;

      const timeoutID = setTimeout(() => {
        reconnectTimeouts.delete(timeoutID);

        if (!isAlive) return;
        if (!shouldBootRealtime) return;
        if (websocketManager.isConnected()) return;

        websocketManager.connect(token, "presence");
        reconnectAttempts += 1;
      }, delay + jitter);

      reconnectTimeouts.add(timeoutID);
    };

    connectWebSocket();

    const reconnectInterval = setInterval(reconnectWebSocket, 5000);

    return () => {
      isAlive = false;

      clearInterval(reconnectInterval);

      reconnectTimeouts.forEach((timeoutID) => {
        clearTimeout(timeoutID);
      });

      reconnectTimeouts.clear();
    };
  }, [shouldBootRealtime, dispatch]);

  if (initialLoading) {
    return (
      <div className="loading-screen">
        <img
          src="/images/logo/TownLITLogo.png"
          alt="TownLIT Logo"
          className="loading-logo"
        />
      </div>
    );
  }

  /*
   * Prevent a private page from flashing while router.replace is moving an
   * incomplete user to /choose-language.
   */
  if (shouldBlockForLanguageRedirect) {
    return (
      <div className="loading-screen">
        <img
          src="/images/logo/TownLITLogo.png"
          alt="TownLIT Logo"
          className="loading-logo"
        />
      </div>
    );
  }

  if (shouldBlockRestrictedPath) {
    return (
      <div className="loading-screen">
        <img
          src="/images/logo/TownLITLogo.png"
          alt="TownLIT Logo"
          className="loading-logo"
        />
      </div>
    );
  }

  const effectiveCanUseAppChrome = Boolean(
    canUseAppChrome &&
    !shouldBlockRestrictedPath &&
    !requiresLanguageOnboarding,
  );

  return (
    <div
      className="townlit-root-container websocket-status"
      data-status={isWebSocketConnected ? "connected" : "disconnected"}
      role="status"
      aria-label={
        isWebSocketConnected
          ? "WebSocket connected"
          : webSocketError || "WebSocket disconnected"
      }
    >
      <div
        className={`scroll-wrapper ${
          effectiveCanUseAppChrome ? "with-icon-bar" : ""
        }`}
      >
        {requiresLanguageOnboarding ? (
          <PublicLayout>{kids}</PublicLayout>
        ) : isNoRedirectPath ? (
          <>{kids}</>
        ) : effectiveCanUseAppChrome ? (
          <PrivateLayout>{kids}</PrivateLayout>
        ) : (
          <PublicLayout>{kids}</PublicLayout>
        )}

        {canRunPrivateIntegrations && <DeviceKeyFlowManager />}

        {effectiveCanUseAppChrome &&
          !isRestrictionResolutionPending &&
          !isRestrictedOwnerProfile && (
            <div className="icon-bar-sticky">
              <IconBar />
            </div>
          )}
      </div>
    </div>
  );
}
